import { FC, useEffect, useRef, useState } from 'react'
import { ButtonType, TipoReceitaSncr } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button, InfoAlert, Input, Label, useModal } from 'app/components'
import {
  ROTULO_TIPO_RECEITA_SNCR,
  SncrContexto,
  SncrRetiradaResultado,
  isNotificacaoSncr,
  saldoDoTipo,
} from '../core/_models'
import { retirarBlocoSncr } from '../core/_requests'
import { ErroTraduzido, traduzirErroSncr } from '../core/erros'
import {
  EsperaToken,
  SNCR_POPUP_BLOQUEADA,
  abrirLoginGovbr,
  aguardarTokenGovbr,
} from '../core/govbr'

type Etapa = 'pronto' | 'aguardando' | 'retirando'

type IProps = {
  idEmpresa: number
  tipo: TipoReceitaSncr
  contexto: SncrContexto
  onConfirm: (resultado: SncrRetiradaResultado) => void
}

/**
 * Retirada de numeração SNCR para o profissional autenticado: um bloco de RCE/RET ou números
 * avulsos de Notificação de Receita (quantidade escolhida aqui). O clique em "Entrar com gov.br"
 * abre a janela de login de forma síncrona (bloqueador de popup), espera a ponte devolver o token
 * e chama o backend com ele na mesma hora (o token vale 5 min).
 */
export const SncrRetiradaModal: FC<IProps> = ({ idEmpresa, tipo, contexto, onConfirm }) => {
  const { t } = useT()
  const { closeModal } = useModal()
  const [etapa, setEtapa] = useState<Etapa>('pronto')
  const [erro, setErro] = useState<ErroTraduzido | null>(null)
  const [quantidade, setQuantidade] = useState<number>(contexto.quantidadeNotificacaoMaxima)
  const esperaRef = useRef<EsperaToken | null>(null)
  // Falso depois que a modal fechou (X durante a espera/retirada): nada de setState nem closeModal,
  // que fecharia outra modal aberta no meio tempo — o ModalContext é único.
  const ativoRef = useRef(true)

  const notificacao = isNotificacaoSncr(tipo)
  const saldo = saldoDoTipo(contexto, tipo)
  const blocosNoMes = saldo?.blocosNoMes ?? 0
  // O limite mensal é dos blocos; a notificação tem limite diário e quem o aplica é a Anvisa.
  const limiteAtingido = !notificacao && blocosNoMes >= contexto.limiteBlocosMes
  const { quantidadeNotificacaoMinima: minimo, quantidadeNotificacaoMaxima: maximo } = contexto
  const quantidadeValida =
    !notificacao || (Number.isInteger(quantidade) && quantidade >= minimo && quantidade <= maximo)
  const rotuloTipo = ROTULO_TIPO_RECEITA_SNCR[tipo]
  const ocupado = etapa !== 'pronto'

  useEffect(
    () => () => {
      ativoRef.current = false
      esperaRef.current?.cancelar()
    },
    []
  )

  const traduzirErro = (error: unknown) => traduzirErroSncr(error, t)

  const retirar = async () => {
    setErro(null)
    if (!contexto.urlLogin) {
      setErro({ mensagem: t('sncr.erro.sem_url_login'), podeRepetir: false })
      return
    }
    // window.open precisa acontecer aqui, antes de qualquer await.
    const popup = abrirLoginGovbr(contexto.urlLogin)
    if (!popup) {
      setErro(traduzirErro(new Error(SNCR_POPUP_BLOQUEADA)))
      return
    }
    setEtapa('aguardando')
    const espera = aguardarTokenGovbr(popup)
    esperaRef.current = espera
    try {
      const token = await espera.promise
      if (!ativoRef.current) return
      setEtapa('retirando')
      const resultado = await retirarBlocoSncr(
        idEmpresa,
        notificacao ? { token, tipo, quantidade } : { token, tipo }
      )
      // Mesmo com a modal já fechada o bloco foi debitado na Anvisa: onConfirm invalida o contexto.
      onConfirm(resultado)
      if (ativoRef.current) closeModal()
    } catch (error) {
      if (!ativoRef.current) return
      setErro(traduzirErro(error))
      setEtapa('pronto')
    } finally {
      esperaRef.current = null
    }
  }

  const cancelar = () => {
    esperaRef.current?.cancelar()
    closeModal()
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <p className='mb-0'>
        {notificacao
          ? t('sncr.modal.intro_nr', { tipo: rotuloTipo, sigla: tipo, minimo, maximo })
          : t('sncr.modal.intro', {
              tipo: rotuloTipo,
              sigla: tipo,
              quantidade: contexto.tamanhoBloco,
            })}
      </p>

      <dl className='row mb-0'>
        {contexto.inscricao && (
          <>
            <dt className='col-sm-5 text-muted fw-normal'>{t('sncr.modal.inscricao')}</dt>
            <dd className='col-sm-7 fw-bold'>{contexto.inscricao}</dd>
          </>
        )}
        <dt className='col-sm-5 text-muted fw-normal'>
          {t('sncr.modal.saldo_atual', { sigla: tipo })}
        </dt>
        <dd className='col-sm-7 fw-bold'>
          {t('sncr.modal.numeros', { quantidade: saldo?.saldo ?? 0 })}
        </dd>
        {!notificacao && (
          <>
            <dt className='col-sm-5 text-muted fw-normal'>{t('sncr.modal.blocos_no_mes')}</dt>
            <dd className='col-sm-7 fw-bold'>
              {t('sncr.modal.blocos_de', { atual: blocosNoMes, limite: contexto.limiteBlocosMes })}
            </dd>
          </>
        )}
      </dl>

      {notificacao && (
        <div>
          <Label htmlFor='sncr-quantidade' className='mb-2'>
            {t('sncr.modal.quantidade')}
          </Label>
          <Input
            id='sncr-quantidade'
            name='quantidade'
            type='number'
            inputMode='numeric'
            min={minimo}
            max={maximo}
            step='1'
            placeholder=''
            value={Number.isNaN(quantidade) ? '' : quantidade}
            onChange={(e) => setQuantidade(e.target.valueAsNumber)}
            disabled={ocupado}
            isInvalid={!quantidadeValida}
            errorMessage={t('sncr.modal.quantidade_invalida', { minimo, maximo })}
            aria-describedby='sncr-quantidade-ajuda'
            aria-invalid={!quantidadeValida}
          />
          <div id='sncr-quantidade-ajuda' className='form-text'>
            {t('sncr.modal.quantidade_ajuda', { minimo, maximo })}
          </div>
        </div>
      )}

      <InfoAlert variant={limiteAtingido ? 'warning' : 'info'} role='note' className='mb-0'>
        {notificacao
          ? t('sncr.modal.aviso_nr')
          : limiteAtingido
          ? t('sncr.modal.aviso_limite_atingido', { limite: contexto.limiteBlocosMes })
          : t('sncr.modal.aviso_limite', { limite: contexto.limiteBlocosMes })}
      </InfoAlert>

      {etapa !== 'pronto' && (
        <div className='d-flex align-items-center gap-3' role='status' aria-live='polite'>
          <span className='spinner-border spinner-border-sm text-primary' aria-hidden='true' />
          <span>
            {etapa === 'aguardando'
              ? t('sncr.modal.aguardando_govbr')
              : notificacao
              ? t('sncr.modal.retirando_nr')
              : t('sncr.modal.retirando')}
          </span>
        </div>
      )}

      {erro && (
        <InfoAlert variant='danger' role='alert' className='mb-0'>
          {erro.mensagem}
        </InfoAlert>
      )}

      <div className='d-flex justify-content-end gap-2 mt-2'>
        <Button className='btn-secondary' onClick={cancelar} type={ButtonType.Button}>
          {t('sncr.modal.cancelar')}
        </Button>
        <Button
          className='btn-primary'
          type={ButtonType.Button}
          onClick={retirar}
          disabled={
            ocupado || limiteAtingido || !quantidadeValida || (erro !== null && !erro.podeRepetir)
          }
          ariaLabel={t(
            notificacao ? 'sncr.modal.entrar_govbr_aria_nr' : 'sncr.modal.entrar_govbr_aria',
            { sigla: tipo }
          )}
        >
          {erro && erro.podeRepetir
            ? t('sncr.modal.tentar_novamente')
            : notificacao
            ? t('sncr.modal.entrar_govbr_nr')
            : t('sncr.modal.entrar_govbr')}
        </Button>
      </div>
    </div>
  )
}
